import "@fontsource/inter";
import "@fontsource/inter/600.css";

import CircleIcon from "@mui/icons-material/Circle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  SnackbarContent,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import moment from "moment";
import { rgba } from "polished";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { isCookieAuth } from "../../../../../../lib/authProvider";
import {
  advanceBusinessReportSelector,
  changeDefaultAdvanceSelectionReport,
  fetchAdvanceBusinessReport,
  fetchAdvanceBusinessReportEvents,
  fetchOrganicRankTrackingData,
  fetchProductItemDetails,
  fetchSponsoredRankTrackingData,
  resetAdvanceBusinessReport,
  resetCaRankTrackingData,
  resetRankTrackingData,
} from "../../../../../../redux/slices/advance_business_report";
import {
  fetchProductRanks,
  resetAdvancedProductList,
  setStore,
  storeAnalyticsSelector,
} from "../../../../../../redux/slices/store_analytics";
import {
  defaultLevel1AdvancedReport,
  defaultLevel2AdvancedReport,
  defaultPeriodicity,
  metricBackendNameToFrontendName,
  metricValues,
} from "../../../constants";
import { TabPanel } from "../../../helpers";
import TableCustom from "../../StoreProducts/ProductTable";
import { ChartLoader } from "../../chartloader";
import DatePicker from "../../shared/DatePicker";
import { useStores } from "../../shared/Storepicker";

import AddEventForm from "./AddEventForm";
import RankTracking from "./RankTracking";
import RemoveEventForm from "./RemoveEventForm";
import LineChart from "./chart";
import {
  AllSKUsSelect,
  Body,
  CustomCheckbox,
  CustomInput,
  DropdownProgressDiv,
  Gap,
  GraphContainer,
  GraphIndicators,
  Header,
  Level2DropdownAsin,
  Level2DropdownImage,
  Level2DropdownProductName,
  Level2DropdownSku,
  MetricsSelect,
  StyledImageNotSupportedIcon,
} from "./styles";

const PNL_LINES = [
  "spend_sp",
  "spend_display_ads",
  "spend_brand",
  "refunds",
  "promotions",
  "fulfillment_fees",
  "referral_fees",
  "extra_fees",
  "cogs",
];

const DataTable = styled(DataGridPro)({
  ".MuiDataGrid-main>div:nth-child(3)": {
    display: "none",
  },
  // "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
  //   display: "none",
  // },
  "& .MuiDataGrid-pinnedColumns": {
    backgroundColor: "white",
  },
  "& .MuiDataGrid-columnsContainer": {
    backgroundColor: "#fafafa",
  },
  // "& .MuiDataGrid-iconSeparator": {
  //   display: "none",
  // },
  "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
    borderRight: "1px solid #f0f0f0",
  },
  "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
    borderBottom: "1px solid #f0f0f0",
  },
  "& .MuiDataGrid-cell": {
    color: "rgba(0,0,0,.85)",
  },
  "& .MuiDataGrid-cellContent": {
    overflow: "visible",
  },
  "& .MuiPaginationItem-root": {
    borderRadius: 0,
  },
  "& .MuiDataGrid-footerContainer": {
    display: "none",
  },
});

const CustomAccordion = styled(Accordion)(({ _ }) => {
  return {
    boxShadow: "none", // this styles directly apply to accordion
    // border: "4px solid red",
    ".Mui-expanded": {
      minHeight: 25,
      marginTop: 5,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    ".MuiBox-root": {
      marginTop: 0,
    },
  };
});

const useStyles = makeStyles(() => ({
  menuPaper: {
    maxHeight: "calc(100% - 216px)",
  },
  predictionRow: {
    backgroundColor: "rgb(240, 240, 240)",
    fontStyle: "italic",
    overflow: "visible",
  },
}));
const BusinessReportAdvanced = ({ value }) => {
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = React.useState(0);
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const paramLevel = searchParams.get("level");
  const paramValue = searchParams.get("value");
  const selectedLevel =
    paramLevel === "sku"
      ? "SKU"
      : paramLevel === "parent_asin"
      ? "Parent ASIN"
      : paramLevel === "child_asin"
      ? "Child ASIN"
      : null;
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const metrics = searchParams.get("metrics");

  const dispatch = useDispatch();
  // receive the API response from advance_business_report redux
  const {
    advanceBusinessReportData,
    advanceBusinessReportLoading,
    productItemDetailsList,
    productItemDetailsLoading,
    defaultAdvanceSelectionReport,
    sponsoredRankTrackingData,
    sponsoredRankTrackingDataLoading,
    caSponsoredRankTrackingData,
    caSponsoredRankTrackingDataLoading,
    organicRankTrackingData,
    organicRankTrackingDataLoading,
    caOrganicRankTrackingData,
    caOrganicRankTrackingDataLoading,
    advanceBusinessReportEventsFlag,
  } = useSelector(advanceBusinessReportSelector);

  const apiRef = useGridApiRef();

  setTimeout(() => {
    if (
      apiRef &&
      apiRef.current &&
      apiRef.current.scrollToIndexes &&
      apiRef.current.getAllColumns
    ) {
      apiRef.current.scrollToIndexes({
        colIndex: apiRef.current.getAllColumns
          ? apiRef.current.getAllColumns().length - 1
          : 0,
      });
    }
  }, 100);

  // Metrics
  const [displayEvents, setDisplayEvents] = useState(true);
  const [displayDeals, setDisplayDeals] = useState(true);
  const [currentMetric, setCurrentMetric] = useState(["units", "avg_price"]);
  const [metricList, setMetricList] = useState(metricValues);
  const [svMetricList, setSvMetricList] = useState([]);
  const [list1, setList1] = useState([]);
  const marketRanks = ["US", "CA"];
  const [marketPlace, setMarketPlace] = React.useState("us");
  const [csv_headers, setCsv_headers] = useState([]);
  const [advBusinessData, setAdvBusinessData] = useState();
  const [chartData, setChartData] = useState({
    metric: "",
    labels: "",
    comparedLabels: "",
    dataset: [],
    compareDataset: [],
    events: {},
    comparedEvents: {},
    deals: {},
    comparedDeals: {},
  });
  // dummy data
  const [csvDataset, setCsvDataset] = useState([]);
  const [displayDataset, setDisplayDataset] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [level, setLevel] = useState("");
  const [showCompareGraph, setShowCompareGraph] = useState(false);
  const [searchVolumeBySearchQuery, setSearchVolumeBySearchQuery] = useState(
    {}
  );
  // Periodicity
  const [currentPeriodicity, setCurrentPeriodicity] = useState(
    searchParams.get("periodicity") ?? defaultPeriodicity
  );

  const searchOptions = useMemo(() => {
    if (
      Array.isArray(productItemDetailsList) &&
      productItemDetailsList.length > 0
    ) {
      return productItemDetailsList.filter(
        (option) =>
          option?.product_name
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          (level === "SKU"
            ? option?.sku?.toLowerCase().startsWith(searchText.toLowerCase())
            : level === "Parent ASIN"
            ? option?.parent_asin
                ?.toLowerCase()
                .startsWith(searchText.toLowerCase())
            : option?.child_asin
                ?.toLowerCase()
                .startsWith(searchText.toLowerCase()))
      );
    }
    return [];
  }, [searchText, productItemDetailsList, level]);

  const return_csv_data = useCallback(() => {
    const keys_list = [{ label: "LABEL", key: "label" }];
    const csvMetricKeys = Array.from(
      new Set([
        ...metricList,
        ...PNL_LINES,
        "net_profit",
        "net_margin",
        ...svMetricList,
      ])
    );
    const headers = keys_list.concat(
      csvMetricKeys.map((v) => ({
        label: (metricBackendNameToFrontendName[v] || v).toUpperCase(),
        key: v,
      }))
    );
    setCsv_headers(headers);
  }, [metricList, svMetricList]);

  const _getDataAsList = useCallback(
    (element, advBusinessData) => {
      if (
        searchVolumeBySearchQuery &&
        searchVolumeBySearchQuery.hasOwnProperty(element)
      ) {
        let valueForGraph = [];
        const weekDateToValueDict = searchVolumeBySearchQuery[element];
        let dataCompleteLabels =
          advBusinessData?.business_report?.labels_complete_str;
        if (!dataCompleteLabels) {
          const dataLabels = advBusinessData?.business_report?.labels;
          dataCompleteLabels = dataLabels.map((label) => {
            if (currentPeriodicity === "day") {
              const dd = label.split("-")[0];
              const M = label.split("-")[1];
              let mm = null;
              if (M === "Jan") {
                mm = "01";
              } else if (M === "Feb") {
                mm = "02";
              } else if (M === "Mar") {
                mm = "03";
              } else if (M === "Apr") {
                mm = "04";
              } else if (M === "May") {
                mm = "05";
              } else if (M === "Jun") {
                mm = "06";
              } else if (M === "Jul") {
                mm = "07";
              } else if (M === "Aug") {
                mm = "08";
              } else if (M === "Sep") {
                mm = "09";
              } else if (M === "Oct") {
                mm = "10";
              } else if (M === "Nov") {
                mm = "11";
              } else if (M === "Dec") {
                mm = "12";
              }
              return `2025-${mm}-${dd}`;
            } else if (currentPeriodicity === "week") {
              const firstDayOfWeek = label.split(" - ")[0];
              return firstDayOfWeek;
            } else if (currentPeriodicity === "month") {
              let M = label.split("-")[0];
              let mm = null;
              if (M === "January") {
                mm = "01";
              } else if (M === "February") {
                mm = "02";
              } else if (M === "March") {
                mm = "03";
              } else if (M === "April") {
                mm = "04";
              } else if (M === "May") {
                mm = "05";
              } else if (M === "June") {
                mm = "06";
              } else if (M === "July") {
                mm = "07";
              } else if (M === "August") {
                mm = "08";
              } else if (M === "September") {
                mm = "09";
              } else if (M === "October") {
                mm = "10";
              } else if (M === "November") {
                mm = "11";
              } else if (M === "December") {
                mm = "12";
              }
              let yyyy = label.split("-")[1];
              return `${yyyy}-${mm}-01`;
            }
            return null;
          });
        }

        for (const dateLabel of dataCompleteLabels ?? []) {
          let value = 0;
          if (currentPeriodicity === "day") {
            // For day periodicity: push the value of that day's week divided by 7.
            // Find the Saturday of the week containing this dateLabel (which is a Monday).
            const currentDate = moment(dateLabel);
            const saturdayOfWeek = currentDate
              .clone()
              .add(2, "day")
              .startOf("isoWeek")
              .subtract(2, "day")
              .format("YYYY-MM-DD");
            const weekValue = weekDateToValueDict[saturdayOfWeek] || 0;
            value = parseInt(weekValue / 7);
          } else if (currentPeriodicity === "week") {
            // For week periodicity: push the week's value.
            // Find the Saturday of the week containing this dateLabel (which is a Monday).
            const currentDate = moment(dateLabel);
            const saturdayOfWeek = currentDate
              .clone()
              .subtract(2, "day")
              .format("YYYY-MM-DD");
            value = weekDateToValueDict[saturdayOfWeek] || 0;
          } else if (currentPeriodicity === "month") {
            // For month periodicity: push the sum of the dates of each month.
            // We need to sum all values for dates within this month.
            // Attention: when the date is for example a Saturday 3rd of the month,
            // the volume contemplates some dates of the previous month.
            const currentDate = moment(dateLabel);
            const monthStart = currentDate.clone().startOf("month");
            const monthEnd = currentDate.clone().endOf("month");
            value = 0;
            Object.keys(weekDateToValueDict).forEach((weekDate) => {
              const weekMoment = moment(weekDate);
              if (weekMoment.isBetween(monthStart, monthEnd, null, "[]")) {
                value += weekDateToValueDict[weekDate] || 0;
              }
            });
          }

          valueForGraph.push(value);
        }
        return valueForGraph;
      } else if (advBusinessData?.all_metrics?.includes(element)) {
        return extractData(element, advBusinessData);
      }
      return [];
    },
    [searchVolumeBySearchQuery, currentPeriodicity]
  );

  const buildCsvDataset = useCallback(
    (baseDataset = [], reportData) => {
      return (baseDataset || []).map((row, index) => {
        const nextRow = { ...row };
        const sales = Number(nextRow.sales) || 0;
        const pnlTotal = PNL_LINES.reduce(
          (sum, line) => sum + (Number(nextRow[line]) || 0),
          0
        );
        const netProfit = sales - pnlTotal;

        if (nextRow.net_profit === undefined) {
          nextRow.net_profit = netProfit;
        }
        if (nextRow.net_margin === undefined) {
          nextRow.net_margin = sales
            ? +((netProfit / sales) * 100).toFixed(2)
            : 0;
        }

        svMetricList.forEach((metricKey) => {
          const values = _getDataAsList(metricKey, reportData) || [];
          nextRow[metricKey] = values[index] ?? "";
        });

        return nextRow;
      });
    },
    [_getDataAsList, svMetricList]
  );

  const handleMetricValueChange = useCallback(
    (value, advBusinessData) => {
      let data = {};
      let compareData = {};
      value.forEach((element) => {
        data[element] = _getDataAsList(element, advBusinessData);
        if (showCompareGraph) {
          compareData[element] = extractCompareData(element, advBusinessData);
        }
      });
      let events = {};
      let comparedEvents = {};
      let deals = {};
      let comparedDeals = {};
      let base_business_report = {};
      let base_compared_business_report = {};
      base_business_report = advBusinessData?.business_report;
      base_compared_business_report = advBusinessData?.compared_business_report;
      for (let i = 0; i < base_business_report?.dataset?.length; i++) {
        events[i] = base_business_report?.dataset[i]?.events;
        deals[i] = base_business_report?.dataset[i]?.deals;
        if (showCompareGraph) {
          comparedEvents[i] = base_compared_business_report?.dataset[i]?.events;
          comparedDeals[i] = base_compared_business_report?.dataset[i]?.deals;
        }
      }

      setChartData({
        metric: value,
        dataset: data,
        compareDataset: compareData,
        labels: base_business_report?.labels,
        comparedLabels: base_compared_business_report?.labels,
        events: events,
        comparedEvents: comparedEvents,
        deals: deals,
        comparedDeals: comparedDeals,
      });
      if (base_business_report) {
        setCsvDataset(
          buildCsvDataset(base_business_report?.dataset, advBusinessData)
        );
        setDisplayDataset(transposeMetricsData(base_business_report?.dataset));
      }
    },
    [showCompareGraph, _getDataAsList, buildCsvDataset]
  );

  // Function to handle selected matric values
  const handleMetricChange = useCallback(
    (event) => {
      let {
        target: { value },
      } = event;
      if (value && value.length < 5) {
        if (value.includes("select")) {
          value.splice(0, 1);
        }
        value = value.length === 0 ? ["select"] : value;
        setCurrentMetric(value);
        handleMetricValueChange(value, advBusinessData);
        setGraphHeight(
          (showCompareGraph ? value.length * 2 : value.length) > 4
            ? "22%"
            : "43%"
        );
      }
    },
    [showCompareGraph, advBusinessData, handleMetricValueChange]
  );

  function transposeMetricsData(data) {
    //adding total row
    let totalRow = { label: "Total/Avg" };
    data.forEach((element, index) => {
      Object.entries(element).forEach(([key, value]) => {
        if (index >= data.length - 2 && value === 0) {
          return;
        }
        if (
          [
            "units",
            "sales",
            "spend_sp",
            "spend_display_ads",
            "spend_brand",
            "refunds",
            "promotions",
            "fulfillment_fees",
            "referral_fees",
            "extra_fees",
            "cogs",
          ].includes(key)
        ) {
          totalRow[key] = totalRow[key] ? totalRow[key] + value : value;
        } else if (
          ["avg_price", "cpc_sp", "conversion_rate", "sessions"].includes(key)
        ) {
          totalRow[key] = totalRow[key]
            ? +parseFloat(totalRow[key] + value / data.length).toFixed(2)
            : +parseFloat(value / data.length).toFixed(2);
        } else {
          totalRow[key] = totalRow[key] ? totalRow[key] : "-";
        }
      });
    });

    totalRow["net_profit"] =
      totalRow["sales"] -
      ((totalRow["spend_sp"] || 0) +
        (totalRow["spend_display_ads"] || 0) +
        (totalRow["spend_brand"] || 0) +
        (totalRow["refunds"] || 0) +
        (totalRow["promotions"] || 0) +
        (totalRow["fulfillment_fees"] || 0) +
        (totalRow["referral_fees"] || 0) +
        (totalRow["extra_fees"] || 0) +
        (totalRow["cogs"] || 0));

    totalRow["net_margin"] = (
      (totalRow["net_profit"] / totalRow["sales"]) *
      100
    ).toFixed(2);

    data.push(totalRow);

    //transposing metrics data
    let transposedTable = Object.keys(data[0]).map((label) => {
      const transposedRow = { label };
      data.forEach((row) => {
        transposedRow[row.label] = row[label];
      });
      return transposedRow;
    });
    //formatting the data
    let orderedTable = [];
    let genIndex = 18;
    transposedTable.forEach((element) => {
      if (element.label === "label") {
        orderedTable[0] = element;
      } else if (element.label === "units") {
        orderedTable[1] = element;
      } else if (element.label === "avg_price") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[2] = newElement;
      } else if (element.label === "sales") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[3] = newElement;
      } else if (element.label === "spend_sp") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[4] = newElement;
      } else if (element.label === "spend_display_ads") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[5] = newElement;
      } else if (element.label === "spend_brand") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[6] = newElement;
      } else if (element.label === "refunds") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[7] = newElement;
      } else if (element.label === "promotions") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[8] = newElement;
      } else if (element.label === "fulfillment_fees") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[9] = newElement;
      } else if (element.label === "referral_fees") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[10] = newElement;
      } else if (element.label === "extra_fees") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[11] = newElement;
      } else if (element.label === "cogs") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[12] = newElement;
      } else if (element.label === "net_profit") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[13] = newElement;
      } else if (element.label === "net_margin") {
        const newElement = {};
        for (const key in element) {
          newElement[key] = key === "label" ? element[key] : element[key] + "%";
        }
        orderedTable[14] = newElement;
      } else if (element.label === "cpc_sp") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? "$" + element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[15] = newElement;
      } else if (element.label === "conversion_rate") {
        const newElement = {};
        for (const key in element) {
          newElement[key] = key === "label" ? element[key] : element[key] + "%";
        }
        orderedTable[16] = newElement;
      } else if (element.label === "sessions") {
        const newElement = {};
        for (const key in element) {
          newElement[key] =
            key === "label"
              ? element[key]
              : element[key] !== undefined
              ? element[key]?.toLocaleString?.()
              : "no data";
        }
        orderedTable[17] = newElement;
      } else {
        orderedTable[genIndex] = element;
        genIndex++;
      }
    });
    return orderedTable;
  }

  const processAdvanceBusinessReport = () => {
    let report = structuredClone(advanceBusinessReportData);
    if (typeof report === "string") {
      report = report.replaceAll(":Infinity", ':"Infinity"');
      report = JSON.parse(report);
    }
    let data = {};
    let compareData = {};
    currentMetric.forEach((element) => {
      data[element] = _getDataAsList(element, report);
    });
    if (showCompareGraph) {
      currentMetric.forEach((element) => {
        compareData[element] = extractCompareData(element, report);
      });
    }
    let events = {};
    let comparedEvents = {};
    let deals = {};
    let comparedDeals = {};
    let base_business_report = {};
    let base_compared_business_report = {};
    base_business_report = report?.business_report;
    base_compared_business_report = report?.compared_business_report;
    for (let i = 0; i < base_business_report?.dataset?.length; i++) {
      events[i] = base_business_report?.dataset[i]?.events;
      deals[i] = base_business_report?.dataset[i]?.deals;
      if (showCompareGraph) {
        comparedEvents[i] = base_compared_business_report?.dataset[i]?.events;
        comparedDeals[i] = base_compared_business_report?.dataset[i]?.deals;
      }
    }
    setChartData({
      metric: currentMetric,
      dataset: data,
      compareDataset: compareData,
      labels: base_business_report?.labels,
      comparedLabels: base_compared_business_report?.labels,
      events: events,
      comparedEvents: comparedEvents,
      deals: deals,
      comparedDeals: comparedDeals,
    });
    setMetricList(report?.all_metrics);
    setAdvBusinessData(report);
    setCsvDataset(buildCsvDataset(base_business_report?.dataset, report));
    setDisplayDataset(transposeMetricsData(base_business_report?.dataset));
    setEmptyAdvanceReport(false);
    setLoading(false);
  };

  //  Function which returns the filtered data from dataset
  function extractData(element, advBusinessData) {
    let extractedData = [];
    let base_business_report = {};

    base_business_report = advBusinessData?.business_report;
    base_business_report?.dataset?.forEach((item) => {
      if (advBusinessData.all_metrics.includes(element)) {
        extractedData.push(item[element]);
      }
    });
    return extractedData;
  }
  function extractCompareData(element, advBusinessData) {
    let extractedCompareData = [];
    let base_compared_business_report = {};

    base_compared_business_report = advBusinessData?.compared_business_report;
    base_compared_business_report?.dataset?.forEach((item) => {
      if (advBusinessData.all_metrics.includes(element)) {
        extractedCompareData.push(item[element]);
      }
    });
    return extractedCompareData;
  }

  const { productRanksList, store } = useSelector(storeAnalyticsSelector);
  const [currentStore, setCurrentStore] = useState(store);
  const [rankLoading, setRankLoading] = useState(false);
  const [displayAddEventForm, setDisplayAddEventForm] = useState();

  const lastReportLevels = useMemo(() => {
    if (Object.keys(advanceBusinessReportData).length > 0) {
      const { selected_child_asin, selected_parent_asin, selected_sku } =
        advanceBusinessReportData;
      const lastLevel1 = selected_child_asin
        ? "Child ASIN"
        : selected_parent_asin
        ? "Parent ASIN"
        : "SKU";
      const lastLevel2 =
        selected_child_asin || selected_parent_asin || selected_sku;
      return {
        level1: lastLevel1,
        level2: lastLevel2,
      };
    } else {
      return {
        level1: defaultLevel1AdvancedReport,
        level2: defaultLevel2AdvancedReport,
      };
    }
  }, [advanceBusinessReportData]);

  if (currentStore.endsWith("_client")) {
    setCurrentStore(currentStore.replace("_client", ""));
  }

  // Initializing current dates
  const [currentStartDate, setCurrentStartDate] = useState(
    startDate && endDate
      ? startDate
      : defaultAdvanceSelectionReport.selectedStartDate ||
          moment().subtract(30, "days").format("YYYY-MM-DD")
  );

  const [currentEndDate, setCurrentEndDate] = useState(
    endDate && startDate
      ? endDate
      : defaultAdvanceSelectionReport.selectedEndDate ||
          moment().format("YYYY-MM-DD")
  );

  const [compareStartDate, setCompareStartDate] = useState(undefined);
  const [compareEndDate, setCompareEndDate] = useState(undefined);
  const [emptyAdvanceReport, setEmptyAdvanceReport] = useState(true);

  // levels values
  const [level1, setLevel1] = useState(lastReportLevels.level1);
  const [level2, setLevel2] = useState(lastReportLevels.level2);
  const fetchData = useRef(false);
  const isFirstRun = useRef(true);
  const [isFirstLoadWithParam, setIsFirstLoadWithParam] = useState(
    paramValue ? true : false
  );

  useEffect(() => {
    // currentStore, not store: the raw selector value keeps the "_client"
    // suffix, which is not a store code the backend can resolve.
    if (!currentStore || currentStore === "all") {
      return;
    }
    dispatch(
      fetchProductRanks({
        store: currentStore,
        marketplace: "us",
        start_date: currentStartDate,
        end_date: currentEndDate,
      })
    );
  }, [dispatch, currentStore, currentStartDate, currentEndDate]);

  const listLevel1 = ["Parent ASIN", "Child ASIN", "SKU"];

  const clearData = useCallback(() => {
    setAdvBusinessData(null);
    setDisplayDataset([]);
    dispatch(resetRankTrackingData());
    dispatch(resetCaRankTrackingData());
    dispatch(resetAdvanceBusinessReport());
    dispatch(resetAdvancedProductList());
    setLevel2("Select");
    setLoading(false);
  }, [dispatch]);

  const handlelevel1Change = (event) => {
    const _level1 = event.target.value;
    clearData();
    setLevel1(_level1);
    setLevel2(`Select ${_level1}`);

    const defaultAdvanceReportOption = {
      selectedStartDate: null,
      selectedEndDate: null,
      selectedLevel1: null,
      selectedMetric: ["units", "avg_price"],
      selectedLevel2: null,
    };
    dispatch(changeDefaultAdvanceSelectionReport(defaultAdvanceReportOption));

    // Get product details for advance report page
    dispatch(
      fetchProductItemDetails({
        store: currentStore,
        level: _level1.toLowerCase().replaceAll(" ", "_"),
      })
    );
  };

  const handlelevel2Change = (event) => {
    const _level2 = event.target.value;
    setLevel2(_level2);
    if (compareStartDate) {
      setShowCompareGraph(true);
    } else {
      setShowCompareGraph(false);
    }
    if (
      currentStore !== "all" &&
      level1 !== "Select Level" &&
      !_level2.startsWith("Select")
    ) {
      setDisplayDataset([]);
      dispatch(
        fetchAdvanceBusinessReport({
          store: currentStore,
          sku: level1 === "SKU" ? _level2 : "",
          asin: level1 === "Parent ASIN" ? _level2 : "",
          child_asin: level1 === "Child ASIN" ? _level2 : "",
          start_date: currentStartDate,
          end_date: currentEndDate,
          periodicity: currentPeriodicity,
          compare_start_date: compareStartDate,
          compare_end_date: compareEndDate,
          marketPlace: "us",
        })
      );
      dispatch(
        fetchAdvanceBusinessReportEvents({
          startDate: currentStartDate,
          endDate: currentEndDate,
          store: currentStore,
          sku: level1 === "SKU" ? _level2 : "",
          asin: level1 === "Parent ASIN" ? _level2 : "",
          child_asin: level1 === "Child ASIN" ? _level2 : "",
        })
      );
      setEmptyAdvanceReport(false);
    }
  };

  useEffect(() => {
    if (
      defaultAdvanceSelectionReport &&
      defaultAdvanceSelectionReport.selectedLevel1 &&
      defaultAdvanceSelectionReport.selectedLevel2 &&
      defaultAdvanceSelectionReport.selectedStartDate &&
      defaultAdvanceSelectionReport.selectedEndDate &&
      productItemDetailsList?.length > 0
    ) {
      setCompareStartDate(undefined);
      setCompareEndDate(undefined);
      setShowCompareGraph(false);
      setEmptyAdvanceReport(false);
      if (metrics && metrics !== "select") {
        setCurrentMetric(metrics.split(","));
      } else if (defaultAdvanceSelectionReport.selectedMetric) {
        setCurrentMetric(defaultAdvanceSelectionReport.selectedMetric);
      }
    }
  }, [defaultAdvanceSelectionReport, productItemDetailsList, metrics]);

  useEffect(() => {
    if (currentStore !== advanceBusinessReportData.store) {
      clearData();
    }
    // eslint-disable-next-line
  }, [currentStore]);

  const stores = useStores();

  useEffect(() => {
    if ((!level2 || level2 === "Select") && !isFirstLoadWithParam) {
      let initialStore;
      if (!currentStore || currentStore === "all") {
        // The cookie path asks for a store instead; picking one here would
        // also change the selection every other tab shows.
        if (isCookieAuth()) return;
        if (stores && stores.length > 0) {
          initialStore = stores[0].value;
          if (initialStore === "all") {
            initialStore = stores[1].value;
          }
          setCurrentStore(initialStore);
          dispatch(setStore(initialStore));
        }
      } else {
        initialStore = currentStore;
        // Get product details for advance report page
        dispatch(
          fetchProductItemDetails({
            store: initialStore,
            level: level1.toLowerCase().split(" ").join("_"),
          })
        );
      }
    }
  }, [
    stores,
    currentStore,
    dispatch,
    level1,
    level2,
    clearData,
    isFirstLoadWithParam,
  ]);

  // Set default data when reload or if there are no data selected on screen
  useEffect(() => {
    if (
      currentStore &&
      level2 === "Select" &&
      currentStore !== "all" &&
      // productItemDetailsList &&
      // productItemDetailsList.length > 0 &&
      !searchParams.get("value")
    ) {
      let value;
      setLoading(true);
      if (searchParams.get("level")) {
        setLevel1(selectedLevel);
        if (searchParams.get("level") === "sku") {
          value = defaultStoreProductAdvancedReport[currentStore].sku;
        } else if (searchParams.get("level") === "parent_asin") {
          value = defaultStoreProductAdvancedReport[currentStore].parentAsin;
        } else if (searchParams.get("level") === "child_asin") {
          value = defaultStoreProductAdvancedReport[currentStore].childAsin;
        }
        setLevel2(value);
      }

      const defaultAdvanceReportOption = {
        selectedStartDate: currentStartDate,
        selectedEndDate: currentEndDate,
        selectedLevel1: searchParams.get("level")
          ? searchParams.get("level")
          : defaultLevel1AdvancedReport,
        selectedMetric: ["units", "avg_price"],
        selectedLevel2: value,
        selectedPeriodicity: defaultPeriodicity,
      };

      dispatch(changeDefaultAdvanceSelectionReport(defaultAdvanceReportOption));
      setLoading(false);
    }
  }, [
    dispatch,
    level2,
    productItemDetailsList,
    currentStore,
    searchParams,
    selectedLevel,
    currentStartDate,
    currentEndDate,
  ]);

  // Convert the date into YYYY-MM-DD format to call API
  const handleDateChange = (csd, ced, cosd, coed, pc) => {
    fetchData.current = true;
    if (cosd) {
      setShowCompareGraph(true);
    } else {
      setShowCompareGraph(false);
    }
    const payload = {
      start_date: csd ? moment(csd).format("YYYY-MM-DD") : "",
      end_date: ced ? moment(ced).format("YYYY-MM-DD") : "",
      compare_start_date: cosd ? moment(cosd).format("YYYY-MM-DD") : "",
      compare_end_date: coed ? moment(coed).format("YYYY-MM-DD") : "",
      periodicity: pc,
    };
    setCurrentStartDate(payload["start_date"]);
    setCurrentEndDate(payload["end_date"]);
    setCompareStartDate(payload["compare_start_date"]);
    setCompareEndDate(payload["compare_end_date"]);
    setCurrentPeriodicity(payload["periodicity"]);
    if (
      currentStore !== "all" &&
      level1 !== "Select Level" &&
      !level2.startsWith("Select")
    ) {
      dispatch(
        fetchAdvanceBusinessReport({
          store: currentStore,
          sku: level1 === "SKU" ? level2 : "",
          asin: level1 === "Parent ASIN" ? level2 : "",
          child_asin: level1 === "Child ASIN" ? level2 : "",
          start_date: payload["start_date"],
          end_date: payload["end_date"],
          periodicity: payload["periodicity"],
          compare_start_date: payload["compare_start_date"],
          compare_end_date: payload["compare_end_date"],
          marketPlace: "us",
        })
      );
      fetchAdvanceBusinessReportEvents({
        startDate: currentStartDate,
        endDate: currentEndDate,
        store: currentStore,
        sku: level1 === "SKU" ? level2 : "",
        asin: level1 === "Parent ASIN" ? level2 : "",
        child_asin: level1 === "Child ASIN" ? level2 : "",
      });
    }
  };

  useEffect(() => {
    if (level2.includes("Select")) {
      // setCurrentMetric(["select"]);
      // setMetricList([]);
      setChartData({
        metric: "",
        labels: "",
        comparedLabels: "",
        dataset: [],
        compareDataset: [],
        events: {},
        comparedEvents: {},
        deals: {},
        comparedDeals: {},
      });
    }
  }, [level2]);

  // This useEffect allots the value to advBusinessData once data is received from API
  useEffect(() => {
    if (
      !emptyAdvanceReport &&
      advanceBusinessReportData &&
      Object.keys(advanceBusinessReportData).length &&
      level2 !== "Select"
    ) {
      processAdvanceBusinessReport();
    }
    //eslint-disable-next-line
  }, [
    level2,
    advanceBusinessReportData,
    currentMetric,
    showCompareGraph,
    emptyAdvanceReport,
  ]);

  useEffect(() => {
    if (
      advanceBusinessReportData &&
      Object.keys(advanceBusinessReportData).length
    ) {
      setCurrentStartDate(
        advanceBusinessReportData?.business_report?.start_date
      );
      setCurrentEndDate(advanceBusinessReportData?.business_report?.end_date);

      if (advanceBusinessReportData?.compared_business_report) {
        setCompareStartDate(
          advanceBusinessReportData?.compared_business_report?.start_date
        );
        setCompareEndDate(
          advanceBusinessReportData?.compared_business_report?.end_date
        );
      }
    }
  }, [advanceBusinessReportData]);

  const metricsSections = useMemo(
    () => [
      {
        title: "Profitability",
        metricsList: [
          {
            backendName: "net_profit",
            frontendName: "Net Profit",
          },
          {
            backendName: "net_margin",
            frontendName: "Net Margin",
          },
        ],
      },
      {
        title: "Sales & Revenue",
        metricsList: [
          {
            backendName: "sales",
            frontendName: "Sales",
          },
          {
            backendName: "units",
            frontendName: "Units",
          },
          {
            backendName: "avg_price",
            frontendName: "Avg Price",
          },
          {
            backendName: "items",
            frontendName: "Orders",
          },
          {
            backendName: "units_b2b",
            frontendName: "Units B2B",
          },
          {
            backendName: "items_b2b",
            frontendName: "Orders B2B",
          },
        ],
      },
      {
        title: "Traffic & Conversion",
        metricsList: [
          {
            backendName: "sessions",
            frontendName: "Sessions",
          },
          {
            backendName: "conversion_rate",
            frontendName: "Conversion Rate",
          },
        ],
      },
      {
        title: "Advertising Performance (PPC)",
        metricsList: [
          {
            isSubtitle: true,
            value: "Sponsored Products",
          },
          {
            backendName: "spend_sp",
            frontendName: "Spend",
          },
          {
            backendName: "impressions_sp",
            frontendName: "Impressions",
          },
          {
            backendName: "clicks_sp",
            frontendName: "Clicks",
          },
          {
            backendName: "ctr_sp",
            frontendName: "CTR",
          },
          {
            backendName: "cpc_sp",
            frontendName: "CPC",
          },
          {
            backendName: "purchases_samesku_sp",
            frontendName: "Advertised SKU Purchases",
          },
          {
            backendName: "conversion_rate_samesku_sp",
            frontendName: "Advertised SKU Conversion Rate",
          },
          {
            isSubtitle: true,
            value: "Sponsored Brand",
          },
          {
            backendName: "spend_brand",
            frontendName: "Spend",
          },
          {
            isSubtitle: true,
            value: "Sponsored Display",
          },
          {
            backendName: "spend_display_ads",
            frontendName: "Spend",
          },
          {
            backendName: "impressions_display_ads",
            frontendName: "Impressions",
          },
          {
            backendName: "clicks_display_ads",
            frontendName: "Clicks",
          },
          {
            backendName: "ctr_display_ads",
            frontendName: "CTR",
          },
          {
            backendName: "cpc_display_ads",
            frontendName: "CPC",
          },
          {
            backendName: "purchases_samesku_display_ads",
            frontendName: "Advertised SKU Purchases",
          },
          {
            backendName: "rate_display_ads",
            frontendName: "Rate",
          },
        ],
      },
      {
        title: "Inventory & Operations",
        metricsList: [
          {
            backendName: "needed_velocity",
            frontendName: "Needed Velocity",
          },
        ],
      },
    ],
    []
  );

  useEffect(() => {
    if (
      advanceBusinessReportData &&
      Object.keys(advanceBusinessReportData).length
    ) {
      if (organicRankTrackingData) {
        const searchVolumeBySearchQuery = {};
        let termsToRelevancy = {};
        let relevantTerms = [];
        organicRankTrackingData?.ranks?.ranks?.forEach((rankCell) => {
          const relevancy = rankCell?.relevancy ?? 0;
          termsToRelevancy[rankCell?.search_term] = relevancy || 1000;
          if (
            relevancy !== 0 &&
            !relevantTerms.includes(rankCell?.search_term)
          ) {
            relevantTerms.push(rankCell?.search_term);
          }
        });
        if (Object.keys(termsToRelevancy).length <= 3) {
          relevantTerms = Object.keys(termsToRelevancy);
        }
        relevantTerms.sort((a, b) => termsToRelevancy[a] - termsToRelevancy[b]);
        for (const relevantTerm of relevantTerms) {
          if (
            productRanksList &&
            level2 &&
            productRanksList[level2] &&
            productRanksList[level2][relevantTerm] &&
            productRanksList[level2][relevantTerm].historical
          ) {
            searchVolumeBySearchQuery[relevantTerm] =
              productRanksList[level2][relevantTerm].historical;
          }
        }
        /*
        organicRankTrackingData?.ranks?.ranks?.forEach((rankCell) => {
          if (relevantTerms.includes(rankCell?.search_term)) {
            if (!relevantRanksBySearchQuery[rankCell?.search_term]) {
              relevantRanksBySearchQuery[rankCell?.search_term] = {};
            }
            relevantRanksBySearchQuery[rankCell?.search_term][rankCell?.date] =
              rankCell?.search_volume_1w;
          }
        });
        */
        setSvMetricList(relevantTerms);
        if (relevantTerms.length > 0) {
          setSearchVolumeBySearchQuery(searchVolumeBySearchQuery);
        }
      }
    }
  }, [
    advanceBusinessReportData,
    organicRankTrackingData,
    productRanksList,
    level2,
  ]);

  useEffect(() => {
    const list1WithMetricsBySection = [];
    metricsSections.forEach((section) => {
      list1WithMetricsBySection.push({ isTitle: true, value: section.title });
      list1WithMetricsBySection.push(...section.metricsList);
    });
    const missingMetrics = metricList
      .filter(
        (x) => !list1WithMetricsBySection.some((y) => y.backendName === x)
      )
      .map((x) => ({ backendName: x, frontendName: x }));
    if (missingMetrics.length > 0) {
      list1WithMetricsBySection.push({ isTitle: true, value: "Other Metrics" });
      list1WithMetricsBySection.push(...missingMetrics);
    }
    if (svMetricList.length > 0) {
      list1WithMetricsBySection.push({ isTitle: true, value: "Search Volume" });
      list1WithMetricsBySection.push(
        ...svMetricList.map((x) => ({
          backendName: x,
          frontendName: x[0].toUpperCase() + x.slice(1),
        }))
      );
    } else {
      if (level2) {
        list1WithMetricsBySection.push({
          isTitle: true,
          value: "No Search Volume data",
        });
      } else {
        list1WithMetricsBySection.push({
          isTitle: true,
          value: "Search Volume - Select ASIN",
        });
      }
    }
    console.log("list1WithMetricsBySection", list1WithMetricsBySection);
    setList1(list1WithMetricsBySection);
  }, [level1, level2, dispatch, metricList, svMetricList, metricsSections]);

  useEffect(() => {
    const baseDataset = advBusinessData?.business_report?.dataset;
    if (!baseDataset) {
      return;
    }
    setCsvDataset(buildCsvDataset(baseDataset, advBusinessData));
  }, [advBusinessData, buildCsvDataset]);

  useEffect(() => {
    if (selectedLevel && paramValue) {
      setLoading(true);
      setLevel1(selectedLevel);
      dispatch(
        fetchProductItemDetails({
          store: currentStore,
          level: selectedLevel.toLowerCase().split(" ").join("_"),
        })
      ).then(() => {
        setLevel2(paramValue);
        dispatch(
          fetchAdvanceBusinessReport({
            store: currentStore,
            sku: selectedLevel === "SKU" ? paramValue : "",
            asin: selectedLevel === "Parent ASIN" ? paramValue : "",
            child_asin: selectedLevel === "Child ASIN" ? paramValue : "",
            start_date: currentStartDate,
            end_date: currentEndDate,
            periodicity: currentPeriodicity,
            marketPlace: "us",
          })
        );
        dispatch(
          fetchAdvanceBusinessReportEvents({
            startDate: currentStartDate,
            endDate: currentEndDate,
            store: currentStore,
            sku: level1 === "SKU" ? paramValue : "",
            asin: level1 === "Parent ASIN" ? paramValue : "",
            child_asin: level1 === "Child ASIN" ? paramValue : "",
          })
        );
        setEmptyAdvanceReport(false);
        setLoading(false);
        setIsFirstLoadWithParam(false);
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      (level1 === "Child ASIN" || level1 === "Parent ASIN") &&
      !level2.startsWith("Select") &&
      currentStore &&
      currentStore !== "all" &&
      currentStartDate &&
      currentEndDate
    ) {
      setRankLoading(true);
      const selectedMarket = marketPlace || "us";
      dispatch(
        fetchSponsoredRankTrackingData({
          asin: level1 === "Parent ASIN" ? level2 : "",
          child_asin: level1 === "Child ASIN" ? level2 : "",
          startDate: currentStartDate,
          endDate: currentEndDate,
          periodicity: currentPeriodicity,
          store: currentStore,
          marketPlace: selectedMarket,
        })
      );
      dispatch(
        fetchOrganicRankTrackingData({
          asin: level1 === "Parent ASIN" ? level2 : "",
          child_asin: level1 === "Child ASIN" ? level2 : "",
          startDate: currentStartDate,
          endDate: currentEndDate,
          periodicity: currentPeriodicity,
          store: currentStore,
          marketPlace: selectedMarket,
        })
      );
      setTabValue(0);
    }
    setRankLoading(false);
    // eslint-disable-next-line
  }, [
    dispatch,
    level1,
    currentStore,
    currentStartDate,
    currentEndDate,
    level2,
    currentPeriodicity,
    clearData,
    marketPlace,
  ]);

  const [readmore, setReadmore] = useState(false);
  const handleReadMore = () => {
    setReadmore(true);
  };
  const handleLessMore = () => {
    setReadmore(false);
  };

  const handleDisplayHideEvents = () => {
    setDisplayEvents(!displayEvents);
  };

  const handleDisplayHideDeals = () => {
    setDisplayDeals(!displayDeals);
  };

  useEffect(() => {
    // load last product viewed
    if (
      currentStore !== value ||
      advanceBusinessReportData.store !== currentStore
    ) {
      clearData();
      setCurrentStore(value);
      dispatch(setStore(value));
      setCurrentStartDate(moment().subtract(30, "days").format("YYYY-MM-DD"));
      setCurrentEndDate(moment().format("YYYY-MM-DD"));
    } else {
      processAdvanceBusinessReport();
    }
    // eslint-disable-next-line
  }, [dispatch, value, selectedLevel, startDate, endDate]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
    } else {
      refreshAdvancedBusinessReport();
    }
    // eslint-disable-next-line
  }, [advanceBusinessReportEventsFlag]);

  const chart1Scales = {
    y: {
      ticks: {
        color: "#EC4899",
      },
      beginAtZero: true,
      afterFit(scale) {
        scale.width = 40;
      },
    },
    y1: {
      ticks: {
        color: "#0891B2",
      },
      beginAtZero: true,
      afterFit(scale) {
        scale.width = 40;
      },
    },
    y2: {
      display: false,
    },
    y3: { display: false },
  };

  const chart1ScalesMany = {
    y: {
      ticks: {
        color: "#EC4899",
      },
      beginAtZero: true,
      afterFit(scale) {
        scale.width = 40;
      },
    },
    y1: {
      ticks: {
        color: "#0891B2",
      },
      beginAtZero: true,
      afterFit(scale) {
        scale.width = 40;
      },
    },
    y2: {
      display: false,
    },
    y3: { display: false },
    x: {
      ticks: {
        color: "transparent",
      },
    },
  };

  const chart2Scales = {
    y: { display: false },
    y1: { display: false },
    y2: {
      ticks: {
        color: "#D4D4D4",
      },
      beginAtZero: true,
      afterFit(scale) {
        scale.width = 40;
      },
    },
    y3: {
      ticks: {
        color: "#1A30F8",
      },
      beginAtZero: true,
      afterFit(scale) {
        scale.width = 40;
      },
    },
  };

  const colors = ["#EC4899", "#0891B2", "#D4D4D4", "#1A30F8"];
  // const height = "22%";

  // calculating height of graph box
  const [graphHeight, setGraphHeight] = useState("22%");
  useLayoutEffect(() => {
    var numOfPlotLines = 0;
    if (currentMetric[0] !== "select") {
      numOfPlotLines = showCompareGraph
        ? currentMetric.length * 2
        : currentMetric.length;
    }
    setGraphHeight(numOfPlotLines > 4 ? "22%" : "43%");
  }, [currentMetric, showCompareGraph]);

  // calculating height of the container having both graphs
  const [graphsContainerHeight, setGraphsContainerHeight] = useState(0);
  useLayoutEffect(() => {
    setGraphsContainerHeight(0.6 * window.innerHeight);
  }, []);
  const [open, setOpen] = useState(false);
  const copyContent = async () => {
    setOpen(true);
    try {
      let url = window.location.href.split("/advanced-business-report")[0];
      let pathname = window.location.pathname;
      let level =
        level1 === "Child ASIN"
          ? "child_asin"
          : level1 === "Parent ASIN"
          ? "parent_asin"
          : "sku";
      let value_level = level2;
      let text =
        url +
        pathname +
        "?store=" +
        value +
        "&level=" +
        level +
        "&start_date=" +
        currentStartDate +
        "&end_date=" +
        currentEndDate +
        "&periodicity=" +
        currentPeriodicity;
      if (!value_level.includes("Select")) {
        text += "&value=" + value_level;
      }
      if (currentMetric[0] !== "select") {
        text += "&metrics=" + currentMetric;
      }
      await navigator.clipboard.writeText(text);
    } catch {}
  };
  const handleClose = () => {
    setOpen(false);
  };
  function tabsProps(index) {
    return {
      value: index,
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }
  const handleRankChange = (e) => {
    setMarketPlace(e.target.value);
    setTabValue(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  function getSelectedProduct(level1, level2) {
    let res = {};
    if (level2 !== "Select") {
      res["id_type"] = level1;
      res["id"] = level2;
    } else {
      res = null;
    }
    return res;
  }

  const handleDisplayAddRemoveEvent = () => {
    setDisplayAddEventForm(!displayAddEventForm);
  };

  const refreshAdvancedBusinessReport = () => {
    dispatch(
      fetchAdvanceBusinessReport({
        store: currentStore,
        sku: level1 === "SKU" ? level2 : "",
        asin: level1 === "Parent ASIN" ? level2 : "",
        child_asin: level1 === "Child ASIN" ? level2 : "",
        start_date: currentStartDate,
        end_date: currentEndDate,
        periodicity: currentPeriodicity,
        marketPlace: "us",
      })
    );
    dispatch(
      fetchAdvanceBusinessReportEvents({
        startDate: currentStartDate,
        endDate: currentEndDate,
        store: currentStore,
        sku: level1 === "SKU" ? level2 : "",
        asin: level1 === "Parent ASIN" ? level2 : "",
        child_asin: level1 === "Child ASIN" ? level2 : "",
      })
    );
  };

  if (isCookieAuth() && (!value || value === "all")) {
    return (
      <Typography
        align="center"
        color="text.secondary"
        sx={{ p: 4 }}
        data-testid="advanced-report-pick-store"
      >
        Pick a store to see its advanced report.
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      />
      <Header.Container>
        <Header.Actions.Container>
          {/* Dynamic list for sku */}
          <AllSKUsSelect
            value={level1}
            onChange={handlelevel1Change}
            displayEmpty
            disabled={advanceBusinessReportLoading || loading}
          >
            <MenuItem value="Select Level" selected={true} disabled={true}>
              Select Level
            </MenuItem>
            {listLevel1.map((row, index) => (
              <MenuItem key={index} value={row}>
                {row}
              </MenuItem>
            ))}
          </AllSKUsSelect>
          <AllSKUsSelect
            MenuProps={{ autoFocus: false, sx: { height: 700 } }}
            value={level2}
            onChange={handlelevel2Change}
            displayEmpty
            onClose={() => setSearchText("")}
            renderValue={() => level2}
            disabled={advanceBusinessReportLoading || loading}
          >
            <ListSubheader>
              <TextField
                size="small"
                autoFocus
                placeholder="Search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setLevel(level1);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") {
                    //Prevent auto-selecting item while typing
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
            <MenuItem
              value={
                level1 === "Select Level"
                  ? "Select"
                  : level1 === "SKU"
                  ? "Select SKU"
                  : level1 === "Child ASIN"
                  ? "Select Child ASIN"
                  : "Select Parent ASIN"
              }
              selected={true}
              disabled={true}
            >
              {level1 === "Select Level"
                ? "Select"
                : level1 === "SKU"
                ? "Select SKU"
                : level1 === "Child ASIN"
                ? "Select Child ASIN"
                : "Select Parent ASIN"}
            </MenuItem>
            {productItemDetailsLoading ? (
              <DropdownProgressDiv>
                <CircularProgress />
              </DropdownProgressDiv>
            ) : (
              productItemDetailsList?.length > 0 &&
              searchOptions?.map((row, index) => {
                const level2ObjectKey = level1
                  .toLowerCase()
                  .split(" ")
                  .join("_");
                return (
                  <MenuItem key={index} value={row[level2ObjectKey]}>
                    {row.image.startsWith("http") ? (
                      <Level2DropdownImage src={row.image} alt={row.image} />
                    ) : (
                      <StyledImageNotSupportedIcon />
                    )}
                    <Grid container width={500}>
                      <Grid item xs={12}>
                        <Level2DropdownAsin>
                          {(level2ObjectKey === "sku"
                            ? row["parent_asin"]
                            : row[level2ObjectKey]) || "N/A"}
                        </Level2DropdownAsin>
                        <CircleIcon sx={{ fontSize: 5, color: "#cdcdcd" }} />
                        <Level2DropdownSku>
                          SKU {row.sku || "N/A"}
                        </Level2DropdownSku>
                      </Grid>
                      <Grid item>
                        <Tooltip title={row.product_name}>
                          <Level2DropdownProductName>
                            {row.product_name
                              ? row.product_name.slice(0, 75) + "..."
                              : "N/A"}
                          </Level2DropdownProductName>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </MenuItem>
                );
              })
            )}
          </AllSKUsSelect>
          {/* Dynamic list for all Matrics */}
          <MetricsSelect
            multiple
            value={currentMetric}
            onChange={handleMetricChange}
            MenuProps={{ classes: { paper: classes.menuPaper } }}
            disabled={advanceBusinessReportLoading || loading}
          >
            <MenuItem selected={true} disabled={true} value="select">
              Select Metric
            </MenuItem>
            {list1?.length &&
              list1.map((row, index) => {
                const sxDict = {
                  opacity: "1 !important",
                  paddingLeft: row.isTitle
                    ? "16px"
                    : row.isSubtitle
                    ? "20px"
                    : "24px",
                };
                if (!readmore) {
                  sxDict[":nth-child(n+8)"] = {
                    position: "absolute",
                    zIndex: "-1",
                    visibility: "hidden",
                  };
                }
                if (row.isTitle || row.isSubtitle) {
                  sxDict.color = "blue";
                  sxDict.fontWeight = "bold";
                  return (
                    <MenuItem
                      key={index}
                      value={row.value}
                      selected={true}
                      disabled={true}
                      sx={sxDict}
                    >
                      {row.value}
                    </MenuItem>
                  );
                }
                return (
                  <MenuItem key={index} value={row.backendName} sx={sxDict}>
                    {row.frontendName}
                  </MenuItem>
                );
              })}
            {list1?.length !== 0 &&
              (!readmore ? (
                <MenuItem
                  style={{ color: "gray", justifyContent: "center" }}
                  onClick={handleReadMore}
                >
                  More <KeyboardArrowDownOutlinedIcon />
                </MenuItem>
              ) : (
                <MenuItem
                  style={{ color: "gray", justifyContent: "center" }}
                  onClick={handleLessMore}
                >
                  Close <KeyboardArrowUpOutlinedIcon />
                </MenuItem>
              ))}
          </MetricsSelect>
          <DatePicker
            showPeriodicity={true}
            onDatesSelected={handleDateChange}
            defaultDates={{
              startDate: new Date(currentStartDate),
              endDate: new Date(currentEndDate),
              compareStartDate: new Date(compareStartDate),
              compareEndDate: new Date(compareEndDate),
            }}
            defaultComparePeriod={
              defaultAdvanceSelectionReport ? "nothing" : "last-year"
            }
            defaultPreset={"Last 30 days"}
            disabled={advanceBusinessReportLoading || loading}
            periodicity={currentPeriodicity}
          />
          <Header.Actions.DownloadCSV.Container
            disabled={advanceBusinessReportLoading || loading}
          >
            {advanceBusinessReportLoading || loading ? (
              <div>
                <Header.Actions.DownloadCSV.Icons.Download
                  disabled={advanceBusinessReportLoading || loading}
                />
              </div>
            ) : (
              <CSVLink
                onClick={return_csv_data}
                data={csvDataset || []}
                filename="advance_business_report.csv"
                headers={csv_headers}
              >
                <Tooltip title="Download Report">
                  <Header.Actions.DownloadCSV.Icons.Download
                    disabled={advanceBusinessReportLoading || loading}
                  />
                </Tooltip>
              </CSVLink>
            )}
          </Header.Actions.DownloadCSV.Container>
          <Header.Actions.DownloadCSV.Container
            disabled={advanceBusinessReportLoading || loading}
          >
            {advanceBusinessReportLoading || loading ? (
              <div>
                <Header.Actions.DownloadCSV.Icons.Copy
                  disabled={advanceBusinessReportLoading || loading}
                />
              </div>
            ) : (
              <div onClick={copyContent}>
                <Tooltip title="Copy Report Url">
                  <Header.Actions.DownloadCSV.Icons.Copy
                    disabled={advanceBusinessReportLoading || loading}
                  />
                </Tooltip>
              </div>
            )}
          </Header.Actions.DownloadCSV.Container>
        </Header.Actions.Container>
      </Header.Container>
      <Gap height={20} />
      <CustomAccordion defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panelinventory-content"
          id="panelinventory-header"
          sx={{
            // minHeight: "15px",
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          <b>Inventory</b>
        </AccordionSummary>
        <AccordionDetails
          id="panelinventory-content"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          <TableCustom
            style={{ maxHeight: "220px" }}
            value={{
              current: {
                start_date: currentStartDate,
                end_date: currentEndDate,
              },
              compare: {
                start_date: compareStartDate,
                end_date: compareEndDate,
              },
              product: getSelectedProduct(level1, level2),
              store: currentStore,
              periodicity: currentPeriodicity,
            }}
          />
        </AccordionDetails>
      </CustomAccordion>
      <CustomAccordion
        defaultExpanded={true}
        displayAddEventForm={displayAddEventForm}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panelgraph-content"
          id="panelgraph-header"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          <b>Graph</b>
        </AccordionSummary>
        <AccordionDetails
          id="panelgraph-content"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
            overflowY: "scroll",
          }}
        >
          <Body.Container>
            {advanceBusinessReportLoading || loading ? (
              <>
                <ChartLoader max={500} />
              </>
            ) : (
              advBusinessData && (
                <Box
                  height={graphsContainerHeight}
                  // sx={{
                  //   paddingBottom: "10%",
                  // }}
                >
                  <CustomInput
                    control={
                      <CustomCheckbox
                        disabled={!list1?.length}
                        checked={displayDeals}
                        onChange={handleDisplayHideDeals}
                      />
                    }
                    label={"Display deals"}
                  />
                  <CustomInput
                    control={
                      <CustomCheckbox
                        disabled={!list1?.length}
                        checked={displayEvents}
                        onChange={handleDisplayHideEvents}
                      />
                    }
                    label={"Display events"}
                  />
                  <div
                    style={{
                      display: "inline",
                      verticalAlign: "sub",
                      marginLeft: "32px",
                    }}
                  >
                    <Button
                      variant="contained"
                      style={{
                        padding: "0px 5px 0px 5px",
                        margin: "0px 0px 7px 0px",
                        minWidth: "0px",
                        maxHeight: displayAddEventForm ? "17px" : "19px",
                        borderRadius: "2px",
                        fontSize: displayAddEventForm ? "small" : "smaller",
                      }}
                      onClick={handleDisplayAddRemoveEvent}
                    >
                      {displayAddEventForm ? "X" : "Add/Remove events"}
                    </Button>
                    <div
                      style={{
                        display: displayAddEventForm ? "block" : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <AddEventForm level1={level1} level2={level2} />
                        <RemoveEventForm
                          startDate={currentStartDate}
                          endDate={currentEndDate}
                          store={currentStore}
                        />
                      </div>
                    </div>
                  </div>
                  <GraphIndicators.Container>
                    {currentMetric[0] !== "select" &&
                      currentMetric.map((m, i) => (
                        <GraphIndicators.Wrapper key={i}>
                          <GraphIndicators.Line color={colors[i]} />
                          <GraphIndicators.Text>{m}</GraphIndicators.Text>
                        </GraphIndicators.Wrapper>
                      ))}
                  </GraphIndicators.Container>
                  <Gap height={30} />
                  {showCompareGraph ? (
                    <>
                      <GraphContainer height={graphHeight}>
                        <LineChart
                          dataset={chartData?.dataset}
                          labels={chartData?.labels}
                          scales={
                            currentMetric.length > 2
                              ? chart1ScalesMany
                              : chart1Scales
                          }
                          events={displayEvents ? chartData?.events : {}}
                          deals={displayDeals ? chartData?.deals : {}}
                          periodicity={currentPeriodicity}
                        />
                      </GraphContainer>
                      {currentMetric.length > 2 ? (
                        <GraphContainer height={graphHeight}>
                          <LineChart
                            dataset={chartData?.dataset}
                            labels={chartData?.labels}
                            scales={chart2Scales}
                            events={displayEvents ? chartData?.events : {}}
                            deals={displayDeals ? chartData?.deals : {}}
                            periodicity={currentPeriodicity}
                          />
                        </GraphContainer>
                      ) : (
                        <></>
                      )}
                      <Gap height={20} />
                      <GraphContainer height={graphHeight}>
                        <LineChart
                          dataset={chartData?.compareDataset}
                          labels={chartData?.comparedLabels}
                          scales={
                            currentMetric.length > 2
                              ? chart1ScalesMany
                              : chart1Scales
                          }
                          events={
                            displayEvents ? chartData?.comparedEvents : {}
                          }
                          deals={displayDeals ? chartData?.comparedDeals : {}}
                          periodicity={currentPeriodicity}
                          is_compare={true}
                        />
                      </GraphContainer>
                      {currentMetric.length > 2 ? (
                        <GraphContainer height={graphHeight}>
                          <LineChart
                            dataset={chartData?.compareDataset}
                            labels={chartData?.comparedLabels}
                            scales={chart2Scales}
                            events={
                              displayEvents ? chartData?.comparedEvents : {}
                            }
                            deals={displayDeals ? chartData?.comparedDeals : {}}
                            periodicity={currentPeriodicity}
                            is_compare={true}
                          />
                        </GraphContainer>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <>
                      <GraphContainer
                        height={currentMetric.length > 2 ? "40%" : "90%"}
                      >
                        <LineChart
                          dataset={chartData?.dataset}
                          labels={chartData?.labels}
                          scales={
                            currentMetric.length > 2
                              ? chart1ScalesMany
                              : chart1Scales
                          }
                          events={displayEvents ? chartData?.events : {}}
                          deals={displayDeals ? chartData?.deals : {}}
                          periodicity={currentPeriodicity}
                        />
                      </GraphContainer>
                      {currentMetric.length > 2 ? (
                        <GraphContainer height={"43%"}>
                          <LineChart
                            dataset={chartData?.dataset}
                            labels={chartData?.labels}
                            scales={chart2Scales}
                            events={displayEvents ? chartData?.events : {}}
                            deals={displayDeals ? chartData?.deals : {}}
                            periodicity={currentPeriodicity}
                          />
                        </GraphContainer>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                </Box>
              )
            )}
          </Body.Container>
        </AccordionDetails>
      </CustomAccordion>
      <CustomAccordion defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="paneltable-content"
          id="paneltable-header"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          <b>Data table</b>
        </AccordionSummary>
        <AccordionDetails
          id="paneltable-content"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          {loading || advanceBusinessReportLoading ? (
            <>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                <Skeleton
                  key={item}
                  width={"95%"}
                  sx={{ mt: 5, ml: 5 }}
                  variant="rectangular"
                  height={15}
                />
              ))}
            </>
          ) : displayDataset[0] === undefined ? null : (
            <DataTable
              disableColumnReorder={true}
              rowReordering={true}
              apiRef={apiRef}
              style={{ backgroundColor: "white", height: "500px" }}
              columns={Object.keys(displayDataset[0]).map((item) => ({
                field: item,
                headerName: item,
                width: item === "label" ? 165 : 100,
              }))}
              rows={displayDataset.slice(1)}
              getRowId={(row) => row["label"]}
              initialState={{ pinnedColumns: { left: ["label"] } }}
              hideFooterRowCount
              getRowClassName={(params) => {
                if (PNL_LINES.includes(params.id)) {
                  return classes.predictionRow;
                }
              }}
            />
          )}
        </AccordionDetails>
      </CustomAccordion>
      <CustomAccordion defaultExpanded={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panelrank-content"
          id="panelrank-header"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          <b>Ranking</b>
        </AccordionSummary>
        <AccordionDetails
          id="panelrank-content"
          sx={{
            backgroundColor: rgba(203, 213, 225, 0.5),
          }}
        >
          {rankLoading ||
          sponsoredRankTrackingDataLoading ||
          caSponsoredRankTrackingDataLoading ||
          organicRankTrackingDataLoading ||
          caOrganicRankTrackingDataLoading ? (
            <>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                <Skeleton
                  key={item}
                  width={"95%"}
                  sx={{ mt: 5, ml: 5 }}
                  variant="rectangular"
                  height={15}
                />
              ))}
            </>
          ) : (
            organicRankTrackingData?.ranks &&
            (level1 === "Child ASIN" || level1 === "Parent ASIN") && (
              <Body.Container>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Select
                    value={marketPlace}
                    style={{
                      height: 30,
                      fontSize: "10px",
                      width: 60,
                    }}
                    onChange={handleRankChange}
                  >
                    {marketRanks?.map((place, index) => (
                      <MenuItem
                        key={index}
                        value={place === "US" ? "us" : "ca"}
                      >
                        {place}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label={"Rank Tracking"} {...tabsProps(0)} />
                  <Tab
                    label={"Competitors"}
                    disabled={
                      marketPlace === "ca" ||
                      organicRankTrackingData.ranks.competitors_ranks
                        ?.competitors_asin.length === 0 ||
                      Object.keys(
                        organicRankTrackingData.ranks.competitors_ranks?.ranks
                      ).length === 0
                    }
                    {...tabsProps(1)}
                  />
                </Tabs>
                <TabPanel value={tabValue} index={0}>
                  <RankTracking
                    data={organicRankTrackingData}
                    sponsoredData={sponsoredRankTrackingData}
                    ca_data={caOrganicRankTrackingData}
                    caSponsoredData={caSponsoredRankTrackingData}
                    currentPeriodicity={currentPeriodicity}
                    marketPlace={marketPlace}
                  />
                </TabPanel>
                <TabPanel
                  value={tabValue}
                  disabled={marketPlace === "ca"}
                  index={1}
                >
                  <RankTracking
                    type="competitors"
                    data={organicRankTrackingData}
                    sponsoredData={sponsoredRankTrackingData}
                    ca_data={caOrganicRankTrackingData}
                    caSponsoredData={caSponsoredRankTrackingData}
                    marketPlace={marketPlace}
                    currentPeriodicity={currentPeriodicity}
                  />
                </TabPanel>
                <Gap height={30} />
              </Body.Container>
            )
          )}
        </AccordionDetails>
      </CustomAccordion>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={handleClose}
        message="Report url copied"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          sx={{ minWidth: "100px !important" }}
          message="Report url copied"
        />
      </Snackbar>
    </>
  );
};

export default BusinessReportAdvanced;
